import { useEffect, useRef, useState } from "react"
import { BookOpen, ChevronRight, CreditCard, LogOut, MessageSquare, Settings, Sparkles } from "lucide-react"
import { cn } from "@/lib/utils"

function initials(name) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase()
}

export function AvatarDropdown({ userName, userEmail, planName = "Beta", onNavigate }) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleItemClick = (path) => {
    setIsOpen(false)
    onNavigate?.(path)
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Account menu"
        aria-expanded={isOpen}
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-full border text-xs font-medium outline-none transition-all duration-200",
          isOpen
            ? "border-primary bg-primary/10 text-primary ring-2 ring-primary/20"
            : "border-border bg-muted text-foreground hover:border-border/80 hover:bg-muted/80",
        )}
      >
        {initials(userName)}
      </button>

      {isOpen ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 origin-top-right overflow-hidden rounded-xl border border-border bg-card shadow-[0_12px_24px_-8px_rgba(0,0,0,0.15)] animate-in fade-in zoom-in-95 duration-150"
        >
          <div className="bg-background/50 p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-sm font-medium">
                {initials(userName)}
              </div>
              <div className="flex flex-col overflow-hidden">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-foreground">{userName}</span>
                  {planName ? (
                    <span className="shrink-0 rounded border border-primary/20 bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary">
                      {planName}
                    </span>
                  ) : null}
                </div>
                {userEmail ? <span className="truncate text-xs text-muted-foreground">{userEmail}</span> : null}
              </div>
            </div>
          </div>

          <div className="h-px w-full bg-border" />

          <div className="flex flex-col gap-0.5 p-1.5">
            <MenuItem icon={Settings} label="Settings" onClick={() => handleItemClick("settings")} />
            <MenuItem icon={CreditCard} label="Plan & Billing" onClick={() => handleItemClick("billing")} />
            <MenuItem icon={Sparkles} label="AI Usage" onClick={() => handleItemClick("ai-usage")} accent />
          </div>

          <div className="h-px w-full bg-border" />

          <div className="flex flex-col gap-0.5 p-1.5">
            <MenuItem icon={BookOpen} label="Help & Resources" onClick={() => handleItemClick("help")} chevron />
            <MenuItem icon={MessageSquare} label="Contact Support" onClick={() => handleItemClick("contact")} />
          </div>

          <div className="h-px w-full bg-border" />

          <div className="p-1.5">
            <button
              type="button"
              role="menuitem"
              onClick={() => handleItemClick("logout")}
              className="group flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
            >
              <span className="flex items-center gap-2.5">
                <LogOut className="h-4 w-4" />
                <span>Log out</span>
              </span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function MenuItem({ icon: Icon, label, onClick, accent = false, chevron = false }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="group flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted/60"
    >
      <span className="flex items-center gap-2.5">
        <Icon
          className={cn(
            "h-4 w-4 transition-colors",
            accent ? "text-primary" : "text-muted-foreground group-hover:text-foreground",
          )}
        />
        <span>{label}</span>
      </span>
      {chevron ? (
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 opacity-0 transition-opacity group-hover:opacity-100" />
      ) : null}
    </button>
  )
}
